
import { computed, defineComponent, onBeforeMount, reactive, watch } from 'vue'

import ymaps from 'yandex-maps'

// @components
import PageTitle from '@/common/components/UI/PageTitle/index.vue'
import NewMap from '@/common/new-components/NewMap/index.vue'
import Loader from '@/common/components/UI/Loader/index.vue'
import NewTable from '@/common/new-components/NewTable/index.vue'
import FailedFetch from '@/common/components/UI/FailedFetch/index.vue'
import NewLimit from '@/common/new-components/NewLimit/index.vue'
import NewPagination from '@/common/new-components/NewPagination/index.vue'
import NewCheckbox from '@/common/new-components/NewCheckbox/index.vue'
import TableScrollWrapper from '@/common/components/UI/TableScrollWrapper/index.vue'

// @new-ui
import NewButton from '@/ui-kit/NewButton/index.vue'
import NewSelect from '@/ui-kit/NewSelect/index.vue'
import NewInput from '@/ui-kit/NewInput/index.vue'
import NewDatePicker from '@/ui-kit/NewDatePicker/index.vue'

// @module-components
import CourierCard from '@/modules/NewDispatcher/components/CourierCard/index.vue'
import TakesTableRow from '@/modules/NewDispatcher/components/TakesTableRow/index.vue'
import ModalPanel from '@/modules/NewDispatcher/components/ModalPanel/index.vue'
import TakePanelInformation from '@/modules/NewDispatcher/components/TakePanelInformation/index.vue'
import DeliveryPanelInformation from '@/modules/NewDispatcher/components/DeliveryPanelInformation/index.vue'
import AssignOrderModal from '@/modules/NewDispatcher/components/AssignOrderModal/index.vue'
import ChangeTakeDateModal from '@/modules/NewDispatcher/components/ChangeTakeDateModal/index.vue'
import WaitListStatusModal from '@/modules/NewDispatcher/components/WaitListStatusModal/index.vue'
import ShortcomingModal from '@/modules/NewDispatcher/components/ShortcomingModal/index.vue'
import CourierPaymentModal from '@/modules/NewDispatcher/components/CourierPaymentModal/index.vue'

// @composable
import useNotifications from '@/common/composable/useNotifications'
import useMap from '@/common/new-composable/useMap'
import useMapGeoObjects from '@/common/new-composable/useMapGeoObjects'
import useAccount from '@/common/composable/useAccount'
import { useFile } from '@/common/composable/useFile'

// @types
import { Sector } from '@/new-services/types/sectors'
import { CourierScheduleType, TakeCourier } from '@/new-services/types/couriers'
import {
    AssignOrderPayload,
    ChangeOrderTakeDatePayload,
    Comment,
    GetTakeOrdersParams,
    TakeOrder,
    WaitListStatus,
} from '@/new-services/types/takes'
import { DeliveryOrder } from '@/new-services/types/delivery'
import { StatusType } from '@/new-services/types/status-type'
import { CompanyT } from '@/core/types/companies.types'
import { CityT } from '@/core/types/cities.types'
import { OrderPeriod } from '@/new-services/types/order-period'

// @new-services
import sectorsAPI from '@/new-services/sectors.service'
import couriersAPI from '@/new-services/couriers.service'
import takeAPI from '@/new-services/takes.service'
import deliveryAPI from '@/new-services/delivery.service'
import statusTypeAPI from '@/new-services/status-type.service'
import orderPeriodsAPI from '@/new-services/order-period.service'

// @realisations
import { companies } from '@/core/realisations/companies.realisation'
import { cities } from '@/core/realisations/cities.realisation'
import { couriers } from '@/core/realisations/couriers.realisation'
import { printAPI } from '@/core/realisations/print.realisation'

// @constants
const TAKES_COLUMNS = [
    {
        name: 'Действия',
        key: 'actions',
    },
    {
        name: 'Номер заказа',
        key: 'orderNumber',
    },
    {
        name: 'Дата забора (плановая)',
        key: 'takeDate',
    },
    {
        name: 'Дата создания',
        key: 'createdAt',
    },
    {
        name: 'Клиент',
        key: 'companyName',
        withHint: true,
    },
    {
        name: 'Коробка',
        key: 'hasPackType',
    },
    {
        name: 'Адрес',
        key: 'customer',
        customHint: 'address',
    },
    {
        name: 'Контактное лицо',
        key: 'customer',
        additionalKey: 'contacts',
        customHint: 'fullName',
    },
    {
        name: 'Физ. вес',
        key: 'weight',
    },
    {
        name: 'Объемный вес',
        key: 'volumeWeight',
    },
    {
        name: 'Доп. услуги',
        key: 'additional',
    },
    {
        name: 'Кол-во мест',
        key: 'places',
    },
    {
        name: 'Период',
        key: 'period',
    },
    {
        name: 'Забрано в',
        key: 'takenAt',
    },
    {
        name: 'Статус груза',
        key: 'status',
    },
    {
        name: 'Статус ЛО',
        key: 'waitList',
    },
    {
        name: 'Комментарий',
        key: 'waitListComment',
    },
    {
        name: 'Ответ КЦ',
        key: 'waitListId',
    },
    {
        key: 'problems',
        name: 'Проблема',
    },
    {
        name: 'Курьер',
        key: 'courier',
    },
    {
        name: 'Кол-во накладных',
        key: 'invoiceQuantity',
    },
    {
        name: 'Город',
        key: 'city',
    },
    {
        name: 'Сектор',
        key: 'sector',
    },
]
const WAIT_LIST_STATUSES = [
    { id: 16, name: 'Перенос даты' },
    { id: 24, name: 'Неточный адрес' },
    { id: 26, name: 'Отсутствие получателя' },
    { id: 27, name: 'Переадресация' },
    { id: 29, name: 'Возврат отправителю' },
    { id: 30, name: 'Отложена по просьбе получателя' },
    { id: 31, name: 'Поломка авто' },
    { id: 32, name: 'Не успел по графику клиента' },
    { id: 33, name: 'Получатель на звонки не отвечает (отключен)' },
]
const LO_STATUSES = [
    { id: 1, name: 'Да', key: '1' },
    { id: 2, name: 'Нет', key: '0' },
]

// @validation
import useVuelidate from '@vuelidate/core'
import { requiredIf } from '@vuelidate/validators'

// @store
import { useNewDispatcherStore } from '@/modules/NewDispatcher/stores/useNewDispatcherStore'
import { useTablesStore } from '@/modules/TablesModule/store/useTablesStore'

export default defineComponent({
    components: {
        PageTitle,
        NewMap,
        CourierCard,
        Loader,
        NewButton,
        NewSelect,
        NewInput,
        NewTable,
        FailedFetch,
        TakesTableRow,
        ModalPanel,
        TakePanelInformation,
        DeliveryPanelInformation,
        NewLimit,
        NewPagination,
        AssignOrderModal,
        ChangeTakeDateModal,
        WaitListStatusModal,
        ShortcomingModal,
        CourierPaymentModal,
        NewCheckbox,
        TableScrollWrapper,
        NewDatePicker
    },
    setup() {
        const { saveFile } = useFile()
        const new_dispatcher_store = useNewDispatcherStore()
        const tablesStore = useTablesStore()

        const notifications = useNotifications()
        const { clearMap, addGeoObject, setMapCenter } = useMap()
        const map_graphics = useMapGeoObjects()
        const { havePermissions } = useAccount()

        let map_instance = null as ymaps.Map | null

        const state = reactive({
            is_other: false,
            is_opened: true,
            is_map_ready: false,
            dateFrom: null as string | null,
            dateTo: null as string | null,
            incompletedAllTime: 1,
        })

        const comments_state = reactive({
            is_loading: false,
            is_failed: false,
            list: [] as Comment[],
        })

        const sectors_list_state = reactive({
            is_loading: false,
            is_failed: false,
            options: [] as Sector[],
        })

        const couriers_list_state = reactive({
            is_loading: false,
            is_failed: false,
            options: [] as TakeCourier[],
            filters: {
                sectorIds: [] as number[],
                scheduleTypeId: null as number | null,
                statusIds: [] as number[],
                cityId: null as number | null,
            },
            query: null as string | null,
        })

        const untaked_list_state = reactive({
            is_loading: false,
            is_failed: false,
            is_showed: true,
            orders: [] as TakeOrder[],
        })

        const unassigned_list_state = reactive({
            is_loading: false,
            is_failed: false,
            is_showed: true,
            orders: [] as TakeOrder[],
        })

        const undelivered_list_state = reactive({
            is_loading: false,
            is_failed: false,
            is_showed: true,
            orders: [] as DeliveryOrder[],
        })

        const takes_list_state = reactive({
            is_loading: false,
            is_failed: false,
            orders: [] as TakeOrder[],
            filters: {
                current_page: 1,
                total_pages: 1,
                total_items: 20,
                limit: 50,
                dispatcherSectorId: null as number | null,
                statusIds: [] as number[],
                waitListStatusIds: [] as number[],
                courierId: null as number | null,
                companyIds: [] as number[],
                cityId: null as number | null,
                periodId: null as number | null,
                address: null as string | null,
                orderNumber: null as string | null,
                hasPackType: null as number | null,
                waitListComment: null as string | null,
                createdAtFrom: null as string | null,
                createdAtTo: null as string | null
            },
        })

        const take_panel_state = reactive({
            is_opened: false,
            order: null as TakeOrder | null,
        })

        const delivery_panel_state = reactive({
            is_opened: false,
            order: null as DeliveryOrder | null,
        })

        const courier_schedules_state = reactive({
            is_loading: false,
            is_failed: false,
            list: [] as CourierScheduleType[],
        })

        const status_types_state = reactive({
            is_loading: false,
            is_failed: false,
            list: [] as StatusType[],
        })

        const statuses_lo_state = reactive({
            is_loading: false,
            is_failed: false,
            list: [] as WaitListStatus[],
        })

        const assign_order_state = reactive({
            is_opened: false,
            is_loading: false,
            order: null as TakeOrder | null,
            //
            selected_orders: [] as number[],
        })

        const change_take_date_state = reactive({
            is_opened: false,
            is_loading: false,
            order: null as TakeOrder | null,
        })

        const wait_list_state = reactive({
            is_opened: false,
            is_loading: false,
            order: null as TakeOrder | null,
        })

        const shortcoming_state = reactive({
            is_opened: false,
            is_loading: false,
            order: null as TakeOrder | null,
        })

        const payment_state = reactive({
            is_opened: false,
            is_loading: false,
            type: null as number | null,
            order: null as TakeOrder | null,
        })

        const data_state = reactive({
            companies: {
                is_loading: false,
                is_failed: false,
                list: [] as CompanyT[],
            },
            cities: {
                is_loading: false,
                is_failed: false,
                list: [] as CityT[],
            },
            periods: {
                is_loading: false,
                is_failed: false,
                list: [] as OrderPeriod[],
            },
        })

        const report_state = reactive({
            is_loading: false,
        })

        const mass_print_state = reactive({
            is_loading: false,
        })

        const onCheckOrder = (id: number) => {
            const index = assign_order_state.selected_orders.findIndex(
                (order_id) => order_id === id
            )

            if (index < 0) assign_order_state.selected_orders.push(id)
            else assign_order_state.selected_orders.splice(index, 1)
        }

        const openTakePanel = (order: TakeOrder) => {
            take_panel_state.order = { ...order }
            take_panel_state.is_opened = true
        }

        const closeTakePanel = () => {
            take_panel_state.is_opened = false
            take_panel_state.order = null
        }

        // const openDeliveryPanel = (order: DeliveryOrder) => {
        //     delivery_panel_state.order = { ...order }
        //     delivery_panel_state.is_opened = true
        // }

        const closeDeliveryPanel = () => {
            delivery_panel_state.is_opened = false
            delivery_panel_state.order = null
        }

        const drawSector = (sector: Sector) => {
            const polygon = map_graphics.createPolygon({
                coordinates: JSON.parse(sector.coordinates),
                color: sector.color,
                hintContent: `Сектор ${sector.name}`,
            })

            addGeoObject(map_instance as ymaps.Map, polygon)
        }

        const drawSectors = (sectors: Sector[]) => {
            if (!map_instance) return
            sectors.forEach(drawSector)
        }

        const loadDeliverySectors = async (dispatcher_sector_ids: number[]) => {
            // if (!dispatcher_sector_id) return

            try {
                sectors_list_state.is_loading = true

                const response = await sectorsAPI.getSectors({
                    page: 1,
                    limit: 9999,
                    dispatcherSectorIds: dispatcher_sector_ids,
                })

                sectors_list_state.options = response.data.data

                drawSectors(sectors_list_state.options)

                sectors_list_state.is_failed = false
            } catch (error) {
                sectors_list_state.is_failed = true
                console.error(error)
                notifications.notifyError({
                    error,
                    title: 'При загрузке секторов доставки произошла ошибка',
                })
            } finally {
                sectors_list_state.is_loading = false
            }
        }

        const loadCouriers = async (dispatcher_sector_ids: number[]) => {
            try {
                couriers_list_state.is_loading = true

                const params = {
                    page: 1,
                    limit: 9999,
                    dispatcherSectorIds: dispatcher_sector_ids,
                    sectorIds: couriers_list_state.filters.sectorIds,
                    scheduleTypeId: couriers_list_state.filters.scheduleTypeId,
                    statusIds: [2, ...couriers_list_state.filters.statusIds],
                    dateFrom: state.dateFrom as string,
                    dateTo: state.dateTo as string,
                }

                const response = await couriersAPI.getTakeCouriers(params)

                couriers_list_state.options = response.data.data

                couriers_list_state.is_failed = false
            } catch (error) {
                couriers_list_state.is_failed = true
                console.error(error)
                notifications.notifyError({
                    error,
                    title: 'При загрузке списка курьеров произошла ошибка',
                })
            } finally {
                couriers_list_state.is_loading = false
            }
        }

        const onMapReady = (instance: ymaps.Map) => {
            map_instance = instance
            state.is_map_ready = true
        }

        const startAssignUntakedOrderByOrderId = (order_id: number) => {
            const order = untaked_list_state.orders.find(
                (untaked_order) => untaked_order.orderId === order_id
            )
            if (!order) return
            startAssignOrder(order)
        }

        const startChangeDateUntakedOrderByOrderId = (order_id: number) => {
            const order = untaked_list_state.orders.find(
                (untaked_order) => untaked_order.orderId === order_id
            )
            if (!order) return
            startChangeDate(order)
        }

        const startSetWaitListUntakedOrderByOrderId = (order_id: number) => {
            const order = untaked_list_state.orders.find(
                (untaked_order) => untaked_order.orderId === order_id
            )
            if (!order) return
            startWaitList(order)
        }

        function createSingleUntakedOrderLayout(
            balloonContentHeader: string,
            balloonContentBody: string,
            order: TakeOrder
        ) {
            const SingleOrderLayout = window.ymaps.templateLayoutFactory.createClass(
                `
                ${balloonContentHeader}
                ${balloonContentBody}
                <div class="mt-2 flex items-center">
                    <button id="change-date" class="TheButton primary text small">
                        Перенести дату
                    </button>

                    <button id="give-to-courier" class="TheButton primary text small ml-2">
                        Назначить
                    </button>

                    <button id="wait-list" class="TheButton primary text small ml-2">
                        Добавить в ЛО
                    </button>
                </div>
            `,
                {
                    build: function () {
                        // @ts-ignore
                        SingleOrderLayout.superclass.build.call(this)

                        const btn_change_date = document.getElementById('change-date')
                        const btn_give_to_courier = document.getElementById('give-to-courier')
                        const btn_wait_list = document.getElementById('wait-list')

                        if (btn_change_date) {
                            btn_change_date.addEventListener('click', () =>
                                startChangeDateUntakedOrderByOrderId(order.orderId)
                            )
                        }

                        if (btn_give_to_courier) {
                            btn_give_to_courier.addEventListener('click', () =>
                                startAssignUntakedOrderByOrderId(order.orderId)
                            )
                        }

                        if (btn_wait_list) {
                            btn_wait_list.addEventListener('click', () =>
                                startSetWaitListUntakedOrderByOrderId(order.orderId)
                            )
                        }
                    },
                    clear: function () {
                        const btn_change_date = document.getElementById('change-date')
                        const btn_give_to_courier = document.getElementById('give-to-courier')
                        const btn_wait_list = document.getElementById('wait-list')

                        if (btn_change_date) {
                            btn_change_date.removeEventListener('click', () =>
                                startChangeDateUntakedOrderByOrderId(order.orderId)
                            )
                        }

                        if (btn_give_to_courier) {
                            btn_give_to_courier.removeEventListener('click', () =>
                                startAssignUntakedOrderByOrderId(order.orderId)
                            )
                        }

                        if (btn_wait_list) {
                            btn_wait_list.removeEventListener('click', () =>
                                startSetWaitListUntakedOrderByOrderId(order.orderId)
                            )
                        }

                        // @ts-ignore
                        SingleOrderLayout.superclass.clear.call(this)
                    },
                }
            )

            return SingleOrderLayout
        }

        function createUntakedOrderItemLayout() {
            const OrderItemLayout = window.ymaps.templateLayoutFactory.createClass(
                '{{ properties.balloonContentHeader|raw }}' +
                    '{{ properties.balloonContentBody|raw }}' +
                    `<div class="mt-2 flex flex-col items-center">
                        {% if properties.isCanChangeDate %}
                            <button id="change-date" data-orderid="{{ properties.orderId|raw }}" class="TheButton primary text small">
                                Перенести дату
                            </button>
                        {% endif %}

                        <button id="give-to-courier" data-orderid="{{ properties.orderId|raw }}" class="TheButton primary text small mt-2">
                            Назначить
                        </button>

                        <button id="wait-list" data-orderid="{{ properties.orderId|raw }}" class="TheButton primary text small mt-2">
                            Добавить в ЛО
                        </button>
                    </div>`,
                {
                    build: function () {
                        // @ts-ignore
                        OrderItemLayout.superclass.build.call(this)

                        const btn_change_date = document.getElementById('change-date')
                        const btn_give_to_courier = document.getElementById('give-to-courier')
                        const btn_wait_list = document.getElementById('wait-list')

                        if (btn_change_date) {
                            const order_id = Number(btn_change_date.dataset.orderid)
                            if (!order_id) return

                            btn_change_date.addEventListener('click', () =>
                                startChangeDateUntakedOrderByOrderId(order_id)
                            )
                        }

                        if (btn_give_to_courier) {
                            const order_id = Number(btn_give_to_courier.dataset.orderid)
                            if (!order_id) return

                            btn_give_to_courier.addEventListener('click', () =>
                                startAssignUntakedOrderByOrderId(order_id)
                            )
                        }

                        if (btn_wait_list) {
                            const order_id = Number(btn_wait_list.dataset.orderid)
                            if (!order_id) return

                            btn_wait_list.addEventListener('click', () =>
                                startSetWaitListUntakedOrderByOrderId(order_id)
                            )
                        }
                    },
                    clear: function () {
                        const btn_change_date = document.getElementById('change-date')
                        const btn_give_to_courier = document.getElementById('give-to-courier')
                        const btn_wait_list = document.getElementById('wait-list')

                        if (btn_change_date) {
                            const order_id = Number(btn_change_date.dataset.orderid)
                            if (!order_id) return

                            btn_change_date.removeEventListener('click', () =>
                                startChangeDateUntakedOrderByOrderId(order_id)
                            )
                        }

                        if (btn_give_to_courier) {
                            const order_id = Number(btn_give_to_courier.dataset.orderid)
                            if (!order_id) return

                            btn_give_to_courier.removeEventListener('click', () =>
                                startAssignUntakedOrderByOrderId(order_id)
                            )
                        }

                        if (btn_wait_list) {
                            const order_id = Number(btn_wait_list.dataset.orderid)
                            if (!order_id) return

                            btn_wait_list.removeEventListener('click', () =>
                                startSetWaitListUntakedOrderByOrderId(order_id)
                            )
                        }

                        // @ts-ignore
                        OrderItemLayout.superclass.clear.call(this)
                    },
                }
            )

            return OrderItemLayout
        }

        const startAssignUnassignedOrderByOrderId = (order_id: number) => {
            const order = unassigned_list_state.orders.find(
                (unassigned_order) => unassigned_order.orderId === order_id
            )
            if (!order) return
            startAssignOrder(order)
        }

        const startChangeDateUnassignedOrderByOrderId = (order_id: number) => {
            const order = unassigned_list_state.orders.find(
                (unassigned_order) => unassigned_order.orderId === order_id
            )
            if (!order) return
            startChangeDate(order)
        }

        const startSetWaitListUnassignedOrderByOrderId = (order_id: number) => {
            const order = unassigned_list_state.orders.find(
                (unassigned_order) => unassigned_order.orderId === order_id
            )
            if (!order) return
            startWaitList(order)
        }

        function createSingleUnassignedOrderLayout(
            balloonContentHeader: string,
            balloonContentBody: string,
            order: TakeOrder
        ) {
            const SingleOrderLayout = window.ymaps.templateLayoutFactory.createClass(
                `
                ${balloonContentHeader}
                ${balloonContentBody}
                <div class="mt-2 flex items-center justify-between">
                    <button id="change-date" class="TheButton primary text small">
                        Перенести дату
                    </button>

                    <button id="give-to-courier" class="TheButton primary text small ml-2">
                        Назначить
                    </button>

                    <button id="wait-list" class="TheButton primary text small ml-2">
                        Добавить в ЛО
                    </button>
                </div>
            `,
                {
                    build: function () {
                        // @ts-ignore
                        SingleOrderLayout.superclass.build.call(this)

                        const btn_change_date = document.getElementById('change-date')
                        const btn_give_to_courier = document.getElementById('give-to-courier')
                        const btn_wait_list = document.getElementById('wait-list')

                        if (btn_change_date) {
                            btn_change_date.addEventListener('click', () =>
                                startChangeDateUnassignedOrderByOrderId(order.orderId)
                            )
                        }

                        if (btn_give_to_courier) {
                            btn_give_to_courier.addEventListener('click', () =>
                                startAssignUnassignedOrderByOrderId(order.orderId)
                            )
                        }

                        if (btn_wait_list) {
                            btn_wait_list.addEventListener('click', () => {
                                startSetWaitListUnassignedOrderByOrderId(order.orderId)
                            })
                        }
                    },
                    clear: function () {
                        const btn_change_date = document.getElementById('change-date')
                        const btn_give_to_courier = document.getElementById('give-to-courier')
                        const btn_wait_list = document.getElementById('wait-list')

                        if (btn_change_date) {
                            btn_change_date.removeEventListener('click', () =>
                                startChangeDateUnassignedOrderByOrderId(order.orderId)
                            )
                        }

                        if (btn_give_to_courier) {
                            btn_give_to_courier.removeEventListener('click', () =>
                                startAssignUnassignedOrderByOrderId(order.orderId)
                            )
                        }

                        if (btn_wait_list) {
                            btn_wait_list.removeEventListener('click', () => {
                                startSetWaitListUnassignedOrderByOrderId(order.orderId)
                            })
                        }

                        // @ts-ignore
                        SingleOrderLayout.superclass.clear.call(this)
                    },
                }
            )

            return SingleOrderLayout
        }

        function createUnassignedOrderItemLayout() {
            const OrderItemLayout = window.ymaps.templateLayoutFactory.createClass(
                '{{ properties.balloonContentHeader|raw }}' +
                    '{{ properties.balloonContentBody|raw }}' +
                    `<div class="mt-2 flex flex-col">
                        {% if properties.isCanChangeDate %}
                            <button id="change-date" data-orderid="{{ properties.orderId|raw }}" class="TheButton primary text small">
                                Перенести дату
                            </button>
                        {% endif %}

                        <button id="give-to-courier" data-orderid="{{ properties.orderId|raw }}" class="TheButton primary text small mt-2">
                            Назначить
                        </button>

                        <button id="wait-list" data-orderid="{{ properties.orderId|raw }}" class="TheButton primary text small mt-2">
                            Добавить в ЛО
                        </button>
                    </div>`,
                {
                    build: function () {
                        // @ts-ignore
                        OrderItemLayout.superclass.build.call(this)

                        const btn_change_date = document.getElementById('change-date')
                        const btn_give_to_courier = document.getElementById('give-to-courier')
                        const btn_wait_list = document.getElementById('wait-list')

                        if (btn_change_date) {
                            const order_id = Number(btn_change_date.dataset.orderid)
                            if (!order_id) return

                            btn_change_date.addEventListener('click', () =>
                                startChangeDateUnassignedOrderByOrderId(order_id)
                            )
                        }

                        if (btn_give_to_courier) {
                            const order_id = Number(btn_give_to_courier.dataset.orderid)
                            if (!order_id) return

                            btn_give_to_courier.addEventListener('click', () =>
                                startAssignUnassignedOrderByOrderId(order_id)
                            )
                        }

                        if (btn_wait_list) {
                            const order_id = Number(btn_wait_list.dataset.orderid)
                            if (!order_id) return

                            btn_wait_list.addEventListener('click', () =>
                                startSetWaitListUnassignedOrderByOrderId(order_id)
                            )
                        }
                    },
                    clear: function () {
                        const btn_change_date = document.getElementById('change-date')
                        const btn_give_to_courier = document.getElementById('give-to-courier')
                        const btn_wait_list = document.getElementById('wait-list')

                        if (btn_change_date) {
                            const order_id = Number(btn_change_date.dataset.orderid)
                            if (!order_id) return

                            btn_change_date.removeEventListener('click', () =>
                                startChangeDateUnassignedOrderByOrderId(order_id)
                            )
                        }

                        if (btn_give_to_courier) {
                            const order_id = Number(btn_give_to_courier.dataset.orderid)
                            if (!order_id) return

                            btn_give_to_courier.removeEventListener('click', () =>
                                startAssignUnassignedOrderByOrderId(order_id)
                            )
                        }

                        if (btn_wait_list) {
                            const order_id = Number(btn_wait_list.dataset.orderid)
                            if (!order_id) return

                            btn_wait_list.removeEventListener('click', () =>
                                startSetWaitListUnassignedOrderByOrderId(order_id)
                            )
                        }

                        // @ts-ignore
                        OrderItemLayout.superclass.clear.call(this)
                    },
                }
            )

            return OrderItemLayout
        }

        function createSingleUndeliveredOrderLayout(
            balloonContentHeader: string,
            balloonContentBody: string
            // order: DeliveryOrder
        ) {
            const SingleOrderLayout = window.ymaps.templateLayoutFactory.createClass(
                `
                    ${balloonContentHeader}
                    ${balloonContentBody}
                `,
                {
                    build: function () {
                        // @ts-ignore
                        SingleOrderLayout.superclass.build.call(this)
                    },
                    clear: function () {
                        // @ts-ignore
                        SingleOrderLayout.superclass.clear.call(this)
                    },
                }
            )
            return SingleOrderLayout
        }

        // function createUndeliveredOrderItemLayout() {
        //     const OrderItemLayout = window.ymaps.templateLayoutFactory.createClass(
        //         '{{ properties.balloonContentHeader|raw }}' +
        //             '{{ properties.balloonContentBody|raw }}',
        //         {
        //             build: function () {
        //                 // @ts-ignore
        //                 OrderItemLayout.superclass.build.call(this)
        //             },
        //             clear: function () {
        //                 // @ts-ignore
        //                 OrderItemLayout.superclass.clear.call(this)
        //             },
        //         }
        //     )
        //     return OrderItemLayout
        // }

        const getTakeOrderStatusString = (take_order: TakeOrder): string => {
            const status_id = take_order.status ? take_order.status.id : null
            const status = take_order.status ? take_order.status.title : 'Не указан'
            const status_class = 'text-xs font-medium px-3 py-1 rounded text-center'

            let status_style = ''

            if (status_id && status_id === 1) {
                status_style = 'background-color: #FBDDDD; color: #EC5654;'
            } else {
                status_style = 'background-color: #E9FEEE; color: #2DB77B;'
            }

            return `
                <div class="body__label">Статус :</div>
                <div class="body__value ${status_class}" style="${status_style}">
                    <div class="text-black">${status}</div>
                </div>
            `
        }

        const renderUntakedOrders = (take_orders: TakeOrder[]) => {
            const cluster = map_graphics.createCluster({
                preset: 'islands#invertedGreenClusterIcons',
                gridSize: 10,
                maxZoom: 21,
                // @ts-ignore
                clusterBalloonContentLayoutHeight: 250,
                clusterBalloonItemContentLayout: createUntakedOrderItemLayout(),
            })

            const placemarks = [] as ymaps.Placemark[]

            take_orders.forEach((take_order) => {
                const coordinates = [Number(take_order.latitude), Number(take_order.longitude)]

                const address = take_order.customer ? take_order.customer.address : 'Не указан'
                const courier = take_order.courier ? take_order.courier.fullName : 'Не указан'

                const status = getTakeOrderStatusString(take_order)
                const wait_list_status =
                    take_order.waitList && take_order.waitList.state
                        ? take_order.waitList.state.name
                        : 'Не указан'

                const takenAt = take_order.takenAt ? take_order.takenAt : 'Не указан'

                const props = {
                    balloonContentHeader: `<div class="newballoon__header">${take_order.orderNumber}</div>`,
                    balloonContentBody: `<div class="newballoon__body">
                        <div class="grid grid-cols-2 gap-1 items-center">
                            <div class="body__label">Клиент: </div>
                            <div class="body__value">${take_order.companyName}</div>

                            <div class="body__label">Адрес отправителя: </div>
                            <div class="body__value">${address}</div>

                            <div class="body__label">Количество накладных: </div>
                            <div class="body__value">${take_order.invoiceQuantity}</div>

                            <div class="body__label">Дата забора: </div>
                            <div class="body__value">${take_order.takeDate}</div>

                            <div class="body__label">Курьер: </div>
                            <div class="body__value">${courier}</div>

                            ${status}

                            <div class="body__label">Статус ЛО: </div>
                            <div class="body__value text-xs font-medium px-3 py-1 rounded flex items-center justify-center bg-gray-200 text-black">${wait_list_status}</div>

                            <div class="body__label">Забрано в : </div>
                            <div class="body__value">${takenAt}</div>
                        </div>
                    </div>`,
                }

                const balloonContentLayout = createSingleUntakedOrderLayout(
                    props.balloonContentHeader,
                    props.balloonContentBody,
                    take_order
                )

                const placemark = map_graphics.createPoint(
                    coordinates,
                    {
                        iconContent: take_order.orderNumber,
                        orderId: take_order.orderId,
                        isCanChangeDate: true,
                        balloonContentHeader: props.balloonContentHeader,
                        balloonContentBody: props.balloonContentBody,
                    },
                    {
                        preset: 'islands#greenStretchyIcon',
                        balloonContentLayout,
                    }
                )

                // placemark.events.add('click', () => {
                //     openTakePanel(take_order)
                // })

                placemarks.push(placemark)
            })

            cluster.options.set({ clusterDisableClickZoom: true })

            cluster.add(placemarks)
            addGeoObject(map_instance as ymaps.Map, cluster)
        }

        const rerenderOrders = () => {
            clearMap(map_instance as ymaps.Map)
            drawSectors(sectors_list_state.options)

            if (untaked_list_state.is_showed) {
                renderUntakedOrders(untaked_list_state.orders)
            }

            if (unassigned_list_state.is_showed) {
                renderUnassignedOrders(unassigned_list_state.orders)
            }

            if (undelivered_list_state.is_showed) {
                renderUndliveredOrders(undelivered_list_state.orders)
            }
        }

        const toggleUntakedOrders = () => {
            untaked_list_state.is_showed = !untaked_list_state.is_showed
            rerenderOrders()
        }

        const renderUnassignedOrders = (take_orders: TakeOrder[]) => {
            const cluster = map_graphics.createCluster({
                preset: 'islands#invertedBrownClusterIcons',
                gridSize: 80,
                maxZoom: 21,
                // @ts-ignore
                clusterBalloonContentLayoutHeight: 250,
                clusterBalloonItemContentLayout: createUnassignedOrderItemLayout(),
            })

            const placemarks = [] as ymaps.Placemark[]

            take_orders.forEach((take_order) => {
                const coordinates = [Number(take_order.latitude), Number(take_order.longitude)]

                const address = take_order.customer ? take_order.customer.address : 'Не указан'
                const courier = take_order.courier ? take_order.courier.fullName : 'Не указан'

                const status = getTakeOrderStatusString(take_order)
                const wait_list_status =
                    take_order.waitList && take_order.waitList.state
                        ? take_order.waitList.state.name
                        : 'Не указан'

                const takenAt = take_order.takenAt ? take_order.takenAt : 'Не указан'

                const props = {
                    balloonContentHeader: `<div class="newballoon__header">${take_order.orderNumber}</div>`,
                    balloonContentBody: `<div class="newballoon__body">
                        <div class="grid grid-cols-2 gap-1 items-center">
                            <div class="body__label">Клиент: </div>
                            <div class="body__value">${take_order.companyName}</div>

                            <div class="body__label">Адрес отправителя: </div>
                            <div class="body__value">${address}</div>

                            <div class="body__label">Количество накладных: </div>
                            <div class="body__value">${take_order.invoiceQuantity}</div>

                            <div class="body__label">Дата забора: </div>
                            <div class="body__value">${take_order.takeDate}</div>

                            <div class="body__label">Курьер: </div>
                            <div class="body__value">${courier}</div>

                            ${status}

                            <div class="body__label">Статус ЛО: </div>
                            <div class="body__value text-xs font-medium px-3 py-1 rounded flex items-center justify-center bg-gray-200 text-black">${wait_list_status}</div>

                            <div class="body__label">Забрано в : </div>
                            <div class="body__value">${takenAt}</div>
                        </div>
                    </div>`,
                }

                const balloonContentLayout = createSingleUnassignedOrderLayout(
                    props.balloonContentHeader,
                    props.balloonContentBody,
                    take_order
                )

                const placemark = map_graphics.createPoint(
                    coordinates,
                    {
                        iconContent: take_order.orderNumber,
                        orderId: take_order.orderId,
                        isCanChangeDate: true,
                        balloonContentHeader: props.balloonContentHeader,
                        balloonContentBody: props.balloonContentBody,
                    },
                    {
                        preset: 'islands#brownStretchyIcon',
                        balloonContentLayout,
                    }
                )

                // placemark.events.add('click', (event: any) => {
                //     openTakePanel(take_order)
                // })

                placemarks.push(placemark)
            })

            cluster.options.set({ clusterDisableClickZoom: true })

            cluster.add(placemarks)
            addGeoObject(map_instance as ymaps.Map, cluster)
        }

        const toggleUnassignedOrders = () => {
            unassigned_list_state.is_showed = !unassigned_list_state.is_showed
            rerenderOrders()
        }

        const renderUndliveredOrders = (delivery_orders: DeliveryOrder[]) => {
            const cluster = map_graphics.createCluster({
                preset: 'islands#invertedBlueClusterIcons',
                gridSize: 80,
                maxZoom: 21,
                // clusterBalloonContentLayoutHeight: 250,
                // clusterBalloonItemContentLayout: createUndeliveredOrderItemLayout(),
            })

            const placemarks = [] as ymaps.Placemark[]

            delivery_orders.forEach((delivery_order) => {
                const coordinates = [
                    Number(delivery_order.latitude),
                    Number(delivery_order.longitude),
                ]
                const address = delivery_order.customer
                    ? delivery_order.customer.address
                    : 'Не указан'
                const courier = delivery_order.courier
                    ? delivery_order.courier.fullName
                    : 'Не указан'
                const status = delivery_order.status ? delivery_order.status.title : 'Не указан'
                const deliveredAt = delivery_order.deliveredAt ? delivery_order.deliveredAt : '-'

                const props = {
                    balloonContentHeader: `<div class="newballoon__header">${delivery_order.invoiceNumber}</div>`,
                    balloonContentBody: `<div class="newballoon__body">
                        <div class="grid grid-cols-2 gap-1 items-center">
                            <div class="body__label">Клиент: </div>
                            <div class="body__value">${delivery_order.companyName}</div>

                            <div class="body__label">Адрес получателя: </div>
                            <div class="body__value">${address}</div>

                            <div class="body__label">Курьер: </div>
                            <div class="body__value">${courier}</div>

                            <div class="body__label">Статус : </div>
                            <div class="body__value">${status}</div>

                            <div class="body__label">Доставлено в : </div>
                            <div class="body__value">${deliveredAt}</div>
                        </div>
                    </div>`,
                }

                const balloonContentLayout = createSingleUndeliveredOrderLayout(
                    props.balloonContentHeader,
                    props.balloonContentBody
                    // delivery_order
                )

                const placemark = map_graphics.createPoint(
                    coordinates,
                    {
                        iconContent: delivery_order.invoiceNumber,
                        balloonContentHeader: props.balloonContentHeader,
                        balloonContentBody: props.balloonContentBody,
                    },
                    {
                        // hasBalloon: false,
                        preset: 'islands#blueStretchyIcon',
                        balloonContentLayout,
                    }
                )

                // placemark.events.add('click', (event: any) => {
                //     openDeliveryPanel(delivery_order)
                // })

                placemarks.push(placemark)
            })

            cluster.options.set({ clusterDisableClickZoom: true })

            cluster.add(placemarks)
            addGeoObject(map_instance as ymaps.Map, cluster)
        }

        const toggleUndeliveredOrders = () => {
            undelivered_list_state.is_showed = !undelivered_list_state.is_showed
            rerenderOrders()
        }

        const loadUntakedOrders = async (dateFrom: string, dateTo: string) => {
            // new_dispatcher_store.validation$.$touch()
            // if (new_dispatcher_store.validation$.$invalid) return

            try {
                untaked_list_state.is_loading = true

                const params: GetTakeOrdersParams = {
                    page: 1,
                    limit: 9999,
                    dateFrom,
                    dateTo,
                    dispatcherSectorIds: new_dispatcher_store.dispatcherSectorIds,
                    statusIds: [2],
                    notInStatusIds: [5],
                }

                params.withoutSectorId = new_dispatcher_store.isWithoutSectorId ? 1 : null

                const response = await takeAPI.getTakeOrders(params)

                untaked_list_state.orders = response.data.data

                if (untaked_list_state.is_showed) {
                    renderUntakedOrders(response.data.data)
                }

                untaked_list_state.is_failed = false
            } catch (error) {
                untaked_list_state.is_failed = true
                console.error(error)
                notifications.notifyError({
                    error,
                    title: 'При получении списка незабранных заборов произошла ошибка',
                })
            } finally {
                untaked_list_state.is_loading = false
            }
        }

        const loadUnassignedOrders = async (dateFrom: string, dateTo: string) => {
            // new_dispatcher_store.validation$.$touch()
            // if (new_dispatcher_store.validation$.$invalid) return

            try {
                unassigned_list_state.is_loading = true

                const params: GetTakeOrdersParams = {
                    page: 1,
                    limit: 9999,
                    dateFrom,
                    dateTo,
                    dispatcherSectorIds: new_dispatcher_store.dispatcherSectorIds,
                    statusIds: [1],
                    notInStatusIds: [5],
                }

                params.withoutSectorId = new_dispatcher_store.isWithoutSectorId ? 1 : null

                const response = await takeAPI.getTakeOrders(params)

                unassigned_list_state.orders = response.data.data

                if (unassigned_list_state.is_showed) {
                    renderUnassignedOrders(response.data.data)
                }

                unassigned_list_state.is_failed = false
            } catch (error) {
                unassigned_list_state.is_failed = true
                console.error(error)
                notifications.notifyError({
                    error,
                    title: 'При получении списка неназначенных заборов произошла ошибка',
                })
            } finally {
                unassigned_list_state.is_loading = false
            }
        }

        const loadUndeliveredOrders = async () => {
            // new_dispatcher_store.validation$.$touch()
            // if (new_dispatcher_store.validation$.$invalid) return

            try {
                undelivered_list_state.is_loading = true

                const response = await deliveryAPI.getDeliveryOrders({
                    page: 1,
                    limit: 9999,
                    dispatcherSectorIds: new_dispatcher_store.dispatcherSectorIds,
                    statusIds: [22],
                })

                undelivered_list_state.orders = response.data.data

                if (undelivered_list_state.is_showed) {
                    renderUndliveredOrders(response.data.data)
                }

                undelivered_list_state.is_failed = false
            } catch (error) {
                undelivered_list_state.is_failed = true
                console.error(error)
                notifications.notifyError({
                    error,
                    title: 'При получении списка недоставленных доставок произошла ошибка',
                })
            } finally {
                undelivered_list_state.is_loading = false
            }
        }

        const loadTakeOrders = async () => {
            unselectAllTakes()

            try {
                takes_list_state.is_loading = true

                const waitListComment = state.is_other
                    ? 'other'
                    : takes_list_state.filters.waitListComment
                const params: GetTakeOrdersParams = {
                    page: takes_list_state.filters.current_page,
                    limit: takes_list_state.filters.limit,
                    dateFrom: state.dateFrom,
                    dateTo: state.dateTo,
                    incompletedAllTime: state.incompletedAllTime ? 1 : null,
                    dispatcherSectorIds: new_dispatcher_store.dispatcherSectorIds,
                    statusIds: takes_list_state.filters.statusIds,
                    waitListStatusIds: takes_list_state.filters.waitListStatusIds,
                    hasPackType: takes_list_state.filters.hasPackType,
                    courierId: takes_list_state.filters.courierId,
                    companyIds: takes_list_state.filters.companyIds,
                    cityId: takes_list_state.filters.cityId,
                    periodId: takes_list_state.filters.periodId,
                    address: takes_list_state.filters.address,
                    orderNumber: takes_list_state.filters.orderNumber,
                    createdAtFrom: takes_list_state.filters.createdAtFrom,
                    createdAtTo: takes_list_state.filters.createdAtTo,
                    waitListComment,
                }

                params.withoutSectorId = new_dispatcher_store.isWithoutSectorId ? 1 : null

                const response = await takeAPI.getTakeOrders(params)
                takes_list_state.orders = response.data.data.map((order) => {
                    const isRed = order.problems && order.problems.length
                    return { ...order, id: order.orderId, isRed }
                })

                takes_list_state.filters.total_pages = response.data.meta.last_page
                takes_list_state.filters.total_items = response.data.meta.total

                takes_list_state.is_failed = false
            } catch (error) {
                takes_list_state.is_failed = true
                console.error(error)
                notifications.notifyError({
                    error,
                    title: 'При получении списка заборов произошла ошибка',
                })
            } finally {
                takes_list_state.is_loading = false
            }
        }

        const loadCourierSchedules = async () => {
            try {
                courier_schedules_state.is_loading = true

                const response = await couriersAPI.getCourierScheduleTypes({ limit: 9999 })
                courier_schedules_state.list = response.data.data

                courier_schedules_state.is_failed = false
            } catch (error) {
                console.error(error)
                notifications.notifyError({
                    error,
                    title: 'При получении списка смен курьеров произошла ошибка',
                })
                courier_schedules_state.is_failed = true
            } finally {
                courier_schedules_state.is_loading = false
            }
        }

        const loadStatusTypes = async () => {
            try {
                status_types_state.is_loading = true

                const response = await statusTypeAPI.getStatusTypes({
                    page: 1,
                    limit: 9999,
                    typeId: 1,
                })
                status_types_state.list = response.data.data

                status_types_state.is_failed = false
            } catch (error) {
                console.error(error)
                notifications.notifyError({
                    error,
                    title: 'При получении списка типов статусов произошла ошибка',
                })
                status_types_state.is_failed = true
            } finally {
                status_types_state.is_loading = false
            }
        }

        const loadStatusesLO = async () => {
            try {
                statuses_lo_state.is_loading = true

                const response = await takeAPI.getWaitListStatuses({
                    type: 3,
                    isDispatcher: 1,
                })

                statuses_lo_state.list = response.data.data

                statuses_lo_state.is_failed = false
            } catch (error) {
                console.error(error)
                notifications.notifyError({
                    error,
                    title: 'При получении списка статусов произошла ошибка',
                })
                statuses_lo_state.is_failed = true
            } finally {
                statuses_lo_state.is_loading = false
            }
        }

        const onLimitChange = (new_limit: number) => {
            takes_list_state.filters.limit = new_limit
            loadTakeOrders()
        }

        const onChangePage = (new_page: number) => {
            takes_list_state.filters.current_page = new_page
            loadTakeOrders()
        }

        const resetCouriersFilters = () => {
            couriers_list_state.filters.cityId = null
            couriers_list_state.filters.scheduleTypeId = null
            couriers_list_state.filters.sectorIds = []
            couriers_list_state.filters.statusIds = []
            couriers_list_state.query = null

            // new_dispatcher_store.validation$.$touch()
            // if (new_dispatcher_store.validation$.$invalid) {
            //     couriers_list_state.options = []
            //     return
            // }

            loadCouriers(new_dispatcher_store.dispatcherSectorIds)
        }

        // const reset = () => {
        //     state.dateFrom = null
        //     state.dateTo = null

        //     sectors_list_state.options = []
        //     couriers_list_state.options = []
        //     courier_schedules_state.list = []
        //     undelivered_list_state.orders = []
        //     status_types_state.list = []
        //     untaked_list_state.orders = []
        //     unassigned_list_state.orders = []
        //     takes_list_state.orders = []

        //     takes_list_state.filters.current_page = 1
        //     takes_list_state.filters.total_pages = 1
        //     takes_list_state.filters.total_items = 20
        //     takes_list_state.filters.limit = 50
        //     takes_list_state.filters.statusIds = []
        //     takes_list_state.filters.waitListStatusIds = []
        //     takes_list_state.filters.hasPackType = null
        //     takes_list_state.filters.courierId = null
        //     takes_list_state.filters.companyId = null
        //     takes_list_state.filters.cityId = null
        //     takes_list_state.filters.periodId = null
        //     takes_list_state.filters.address = null
        //     takes_list_state.filters.orderNumber = null

        //     resetCouriersFilters()
        // }

        const loadCompanies = async () => {
            try {
                data_state.companies.is_loading = true

                const response = await companies.getList({
                    page: 1,
                    getAll: 1,
                    isExtended: 0,
                    limit: 9999,
                })
                data_state.companies.list = response.data

                data_state.companies.is_failed = false
            } catch (error) {
                data_state.companies.is_failed = true
                console.error(error)
                notifications.notifyError({
                    error,
                    title: 'При получении списка компаний произошла ошибка',
                })
            } finally {
                data_state.companies.is_loading = false
            }
        }

        const loadCities = async () => {
            try {
                data_state.cities.is_loading = true

                const response = await cities.getAllList(1, undefined, 9999)

                data_state.cities.list = response.data
                data_state.cities.is_failed = false
            } catch (error) {
                data_state.cities.is_failed = true
                console.error(error)
                notifications.notifyError({
                    error,
                    title: 'При получении списка городов произошла ошибка',
                })
            } finally {
                data_state.cities.is_loading = false
            }
        }

        const loadPeriods = async () => {
            try {
                data_state.periods.is_loading = true

                const response = await orderPeriodsAPI.getOrderPeriods({ page: 1, limit: 9999 })
                data_state.periods.list = response.data.data

                data_state.periods.is_failed = false
            } catch (error) {
                data_state.periods.is_failed = true
                console.error(error)
                notifications.notifyError({
                    error,
                    title: 'При получении списка периодов произошла ошибка',
                })
            } finally {
                data_state.periods.is_loading = false
            }
        }

        const loadData = () => {
            clearMap(map_instance as ymaps.Map)

            new_dispatcher_store.dispatcherSectors.forEach((sector) => {
                if (sector.city && sector.city.latitude && sector.city.longitude) {
                    setMapCenter(map_instance as ymaps.Map, [
                        sector.city.latitude,
                        sector.city.longitude,
                    ])
                } else {
                    const centroid = map_graphics.getPolygonCentroid(JSON.parse(sector.coordinates))

                    setMapCenter(map_instance as ymaps.Map, centroid)
                }
            })

            if (isCanGetSectors.value) {
                loadDeliverySectors(new_dispatcher_store.dispatcherSectorIds)
            }

            if (isCanGetCouriers.value) {
                loadCouriers(new_dispatcher_store.dispatcherSectorIds)
                loadCourierSchedules()
            }

            if (isCanGetDeliveries.value) {
                loadUndeliveredOrders()
            }

            loadUntakedOrders(state.dateFrom as string, state.dateTo as string)
            loadUnassignedOrders(state.dateFrom as string, state.dateTo as string)
            loadTakeOrders()
        }

        const resetTakeOrdersFilters = () => {
            takes_list_state.filters.current_page = 1
            takes_list_state.filters.total_pages = 1
            takes_list_state.filters.total_items = 20
            takes_list_state.filters.limit = 50
            takes_list_state.filters.statusIds = []
            takes_list_state.filters.waitListStatusIds = []
            takes_list_state.filters.hasPackType = null
            takes_list_state.filters.courierId = null
            takes_list_state.filters.companyIds = []
            takes_list_state.filters.waitListComment = null
            takes_list_state.filters.cityId = null
            takes_list_state.filters.periodId = null
            takes_list_state.filters.address = null
            takes_list_state.filters.orderNumber = null
            takes_list_state.filters.createdAtFrom = null
            takes_list_state.filters.createdAtTo = null
            state.is_other = false

            loadTakeOrders()
        }

        // New Dispatcher Sector Listener
        watch(
            () => new_dispatcher_store.dispatcherSectorIds,
            () => {
                loadData()
            }
        )

        watch(
            () => new_dispatcher_store.isWithoutSectorId,
            () => {
                if (new_dispatcher_store.isWithoutSectorId) {
                    loadData()
                }
            }
        )

        watch(
            () => state.is_map_ready,
            () => {
                loadData()
            }
        )

        const startAssignOrder = (order: TakeOrder) => {
            assign_order_state.order = { ...order }
            assign_order_state.is_opened = true
        }

        const cancelAssignOrder = () => {
            assign_order_state.is_opened = false
            assign_order_state.order = null
            assign_order_state.selected_orders = []
        }

        const assignOrder = async (courierId: number) => {
            try {
                assign_order_state.is_loading = true

                const payload: AssignOrderPayload = { courierId, orderIds: [] }

                if (assign_order_state.order) {
                    payload.orderIds = [assign_order_state.order.orderId]
                } else {
                    payload.orderIds = assign_order_state.selected_orders
                }

                await takeAPI.assignOrder(payload)

                notifications.notify({ type: 'success', title: 'Курьер назначен' })
                cancelAssignOrder()
                loadData()
            } catch (error) {
                console.error(error)
                notifications.notifyError({
                    error,
                    title: 'При назначении курьера произошла ошибка',
                })
            } finally {
                assign_order_state.is_loading = false
            }
        }

        const startChangeDate = (order: TakeOrder) => {
            change_take_date_state.order = { ...order }
            change_take_date_state.is_opened = true
        }

        const cancelChangeDate = () => {
            change_take_date_state.is_opened = false
            change_take_date_state.order = null
        }

        const changeDate = async (payload: ChangeOrderTakeDatePayload) => {
            try {
                change_take_date_state.is_loading = true

                await takeAPI.changeOrderTakeDate(payload)

                notifications.notify({ type: 'success', title: 'Дата забора сохранена' })
                cancelChangeDate()
                loadData()
            } catch (error) {
                console.error(error)
                notifications.notifyError({
                    error,
                    title: 'При сохранении новой даты забора произошла ошибка',
                })
            } finally {
                change_take_date_state.is_loading = false
            }
        }

        const startWaitList = (order: TakeOrder) => {
            wait_list_state.order = { ...order }
            wait_list_state.is_opened = true
        }

        const cancelWaitList = () => {
            wait_list_state.is_opened = false
            wait_list_state.order = null
        }

        const toggleShortcoming = (order?: TakeOrder) => {
            shortcoming_state.order = order ? { ...order } : null
            shortcoming_state.is_opened = !shortcoming_state.is_opened
        }

        const togglePayment = (type?: number, order?: TakeOrder) => {
            payment_state.order = order ? { ...order } : null
            payment_state.type = type || null
            payment_state.is_opened = !payment_state.is_opened
        }

        const setWaitListStatus = async () => {
            cancelWaitList()
            closeTakePanel()
            loadData()
        }

        const saveShortcoming = async (payload: FormData) => {
            try {
                shortcoming_state.is_loading = true

                await couriers.saveShortcoming(payload)

                notifications.notify({ type: 'success', title: 'Акт успешно загружен' })
                toggleShortcoming()
                closeTakePanel()
                loadData()
            } catch (error) {
                console.error(error)
                notifications.notifyError({
                    error,
                    title: 'При загрузке акта произошла ошибка',
                })
            } finally {
                shortcoming_state.is_loading = false
            }
        }

        const resendOrder = async (order: TakeOrder) => {
            if (!order) return

            try {
                takes_list_state.is_loading = true

                await takeAPI.resendOrder(order.id)

                notifications.notify({ type: 'success', title: 'Заказы прокинуты!' })
                loadData()
            } catch (error) {
                console.error(error)
                notifications.notifyError({
                    error,
                    title: 'Произошла ошибка',
                })
            } finally {
                takes_list_state.is_loading = false
            }
        }

        const savePaymentTakes = async (payload: FormData) => {
            try {
                payment_state.is_loading = true

                await couriers.savePaymentTakes(payload)

                notifications.notify({ type: 'success', title: 'Чеки успешно загружены' })
                togglePayment()
                closeTakePanel()
                loadData()
            } catch (error) {
                console.error(error)
                notifications.notifyError({
                    error,
                    title: 'При загрузке чеков произошла ошибка',
                })
            } finally {
                payment_state.is_loading = false
            }
        }

        const downloadReport = async () => {
            try {
                report_state.is_loading = true

                const waitListComment = state.is_other
                    ? 'other'
                    : takes_list_state.filters.waitListComment
                const params = {
                    dateFrom: state.dateFrom as string,
                    dateTo: state.dateTo as string,
                    dispatcherSectorIds: new_dispatcher_store.dispatcherSectorIds,
                    statusIds: takes_list_state.filters.statusIds,
                    waitListStatusIds: takes_list_state.filters.waitListStatusIds,
                    hasPackType: takes_list_state.filters.hasPackType,
                    courierId: takes_list_state.filters.courierId,
                    companyIds: takes_list_state.filters.companyIds,
                    cityId: takes_list_state.filters.cityId,
                    periodId: takes_list_state.filters.periodId,
                    address: takes_list_state.filters.address,
                    orderNumber: takes_list_state.filters.orderNumber,
                    waitListComment,
                    incompletedAllTime: state.incompletedAllTime ? 1 : null,
                    createdAtFrom: takes_list_state.filters.createdAtFrom,
                    createdAtTo: takes_list_state.filters.createdAtTo
                }

                const response = await takeAPI.downloadReport(params)
                saveFile(response.data, `Отчет (${state.dateFrom} - ${state.dateTo})`)
            } catch (error) {
                console.error(error)
                notifications.notifyError({ error, title: 'При выгрузке отчета произошла ошибка' })
            } finally {
                report_state.is_loading = false
            }
        }

        const downloadMassPdf = async () => {
            if (mass_print_state.is_loading) {
                return
            }

            if (assign_order_state.selected_orders.length === 0) {
                notifications.notify({ type: 'danger', title: 'Нет выбранных заказов' })
                return
            }

            try {
                mass_print_state.is_loading = true

                const params = {
                    orderIds: assign_order_state.selected_orders.map((id) => Number(id)),
                }

                const response = await printAPI.printLtlMassOrders(params)
                saveFile(response, `Массовая печать`)
            } catch (error) {
                console.error(error)
                notifications.notifyError({ error, title: 'При массовой печати произошла ошибка' })
            } finally {
                mass_print_state.is_loading = false
            }
        }

        const closeMapAndCouriers = () => {
            state.is_opened = false
        }

        const openMapAndCouriers = () => {
            state.is_opened = true
        }

        const setMapCenterOnSector = (sector: Sector) => {
            const centroid = map_graphics.getPolygonCentroid(JSON.parse(sector.coordinates))
            setMapCenter(map_instance as ymaps.Map, centroid)
        }

        const toggleSubmitCheckedOrders = () => {
            assign_order_state.is_opened = !assign_order_state.is_opened
        }

        const onSelectTake = (take: TakeOrder) => {
            assign_order_state.selected_orders.push(take.id)
        }

        const onUnselectTake = (take: TakeOrder) => {
            const index = assign_order_state.selected_orders.findIndex(
                (take_id) => take_id === take.id
            )
            if (index >= 0) assign_order_state.selected_orders.splice(index, 1)
        }

        const selectAllTakes = () => {
            assign_order_state.selected_orders = takes_list_state.orders.map((order) => order.id)
        }

        const unselectAllTakes = () => {
            assign_order_state.selected_orders = []
        }

        //
        const couriersList = computed(() => {
            let couriers_list = [...couriers_list_state.options]

            if (couriers_list_state.query) {
                couriers_list = couriers_list.filter((courier) =>
                    courier.fullName
                        .toLowerCase()
                        .includes((couriers_list_state.query as string).toLowerCase())
                )
            }

            if (couriers_list_state.filters.sectorIds.length) {
                couriers_list = couriers_list.filter((courier) =>
                    courier.sectors.find(
                        (sector) =>
                            sector && couriers_list_state.filters.sectorIds.includes(sector.id)
                    )
                )
            }

            return couriers_list
        })

        // const setTodayDate = () => {
        //     let today = new Date().toISOString().slice(0, 10)

        //     state.dateFrom = today
        //     state.dateTo = today
        // }

        const loadComments = async () => {
            try {
                comments_state.is_loading = true

                const response = await takeAPI.getComments(2)
                comments_state.list = response.data.data
                comments_state.list.push({ text: 'Другое' })

                comments_state.is_failed = false
            } catch (error) {
                console.error(error)
                comments_state.is_failed = true
                notifications.notifyError({
                    error,
                    title: 'При получении списка произошла ошибка',
                })
            } finally {
                comments_state.is_loading = false
            }
        }

        const onCommentChange = (value: string) => {
            if (value === 'Другое') state.is_other = true
            else state.is_other = false
        }

        const onIncompletedChange = (value: number) => {
            if (value) {
                state.dateFrom = null
                state.dateTo = null
                takes_list_state.filters.statusIds = []
            }
        }

        // permissions
        const isCanGetStatusTypes = computed(() =>
            havePermissions(['dispatcher.status-type-index', 'all-root-permission'])
        )
        const isCanGetDeliveries = computed(() =>
            havePermissions(['dispatcher.delivery-index', 'all-root-permission'])
        )
        const isCanGetSectors = computed(() =>
            havePermissions(['dispatcher.sector-index', 'all-root-permission'])
        )
        const isCanGetCouriers = computed(() =>
            havePermissions(['dispatcher.courier-index', 'all-root-permission'])
        )

        const isCanAssign = computed(() =>
            havePermissions(['dispatcher.order-take-assign', 'all-root-permission'])
        )
        const isCanChangeDate = computed(() =>
            havePermissions(['dispatcher.order-take-edit', 'all-root-permission'])
        )

        onBeforeMount(() => {
            // setTodayDate()

            if (isCanGetStatusTypes.value) {
                loadStatusTypes()
            }

            loadCompanies()
            loadCities()
            loadPeriods()
            loadStatusesLO()
            loadComments()
        })

        const applyFilters = () => {
            takes_list_state.filters.current_page = 1
            loadTakeOrders()
        }

        return {
            TAKES_COLUMNS,
            WAIT_LIST_STATUSES,
            LO_STATUSES,
            //
            new_dispatcher_store,
            state,
            assign_order_state,
            couriers_list_state,
            sectors_list_state,
            takes_list_state,
            untaked_list_state,
            unassigned_list_state,
            undelivered_list_state,
            courier_schedules_state,
            status_types_state,
            statuses_lo_state,
            data_state,
            report_state,
            mass_print_state,
            comments_state,
            //
            take_panel_state,
            closeTakePanel,
            //
            delivery_panel_state,
            closeDeliveryPanel,
            //
            onCommentChange,
            loadComments,
            setMapCenterOnSector,
            onMapReady,
            loadData,
            loadDeliverySectors,
            loadCourierSchedules,
            loadCouriers,
            loadStatusTypes,
            applyFilters,
            //
            resetCouriersFilters,
            resetTakeOrdersFilters,
            onLimitChange,
            onChangePage,
            //
            startAssignOrder,
            cancelAssignOrder,
            assignOrder,
            //
            change_take_date_state,
            startChangeDate,
            cancelChangeDate,
            changeDate,
            //
            wait_list_state,
            startWaitList,
            cancelWaitList,
            setWaitListStatus,
            //
            closeMapAndCouriers,
            openMapAndCouriers,
            //
            isCanAssign,
            isCanChangeDate,
            //
            couriersList,
            //
            toggleUntakedOrders,
            toggleUnassignedOrders,
            toggleUndeliveredOrders,
            //
            toggleSubmitCheckedOrders,
            onCheckOrder,
            onSelectTake,
            onUnselectTake,
            selectAllTakes,
            unselectAllTakes,
            //
            loadCompanies,
            loadCities,
            loadPeriods,
            downloadReport,
            downloadMassPdf,
            loadTakeOrders,
            openTakePanel,
            loadStatusesLO,
            //
            shortcoming_state,
            toggleShortcoming,
            saveShortcoming,
            //
            payment_state,
            togglePayment,
            savePaymentTakes,
            resendOrder,
            tablesStore,
            onIncompletedChange,
        }
    },
})
